import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";
import React from "react";
import { Bar, BarChart, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

const SalesAnalytics: React.FC = () => {
    const [saleState, setSaleState] = React.useState<"total_sales" | "narcotic_sales">("total_sales");
    const [outputState, setOutputState] = React.useState<"monthly" | "daily">("monthly");
    
    // Sample data for the last 30 days
    const totalSalesData = [
        { day: 1, value: 650 },
        { day: 2, value: 680 },
        { day: 3, value: 720 },
        { day: 4, value: 695 },
        { day: 5, value: 720 },
        { day: 6, value: 520 }, // Weekend dip
        { day: 7, value: 480 }, // Weekend dip
    ];

    const narcoticSalesData = [
        { day: 1, value: 150 },
        { day: 2, value: 165 },
        { day: 3, value: 175 },
        { day: 4, value: 160 },
        { day: 5, value: 180 },
        { day: 6, value: 85 }, // Weekend dip
        { day: 7, value: 75 }, // Weekend dip
    ];

    // Sample data for Monthly/Daily Sale Output
    const monthlySaleOutputData = [
        { period: 1, value: 4.2 },
        { period: 2, value: 3.8 },
        { period: 3, value: 5.1 },
        { period: 4, value: 4.6 },
        { period: 5, value: 5.3 },
        { period: 6, value: 4.9 },
        { period: 7, value: 5.2 },
    ];

    const dailySaleOutputData = [
        { period: 1, value: 2.1 },
        { period: 2, value: 1.9 },
        { period: 3, value: 2.4 },
        { period: 4, value: 2.2 },
        { period: 5, value: 2.6 },
        { period: 6, value: 1.8 }, // Weekend dip
        { period: 7, value: 1.7 }, // Weekend dip
    ];

    const currentData = saleState === "total_sales" ? totalSalesData : narcoticSalesData;
    const currentValue = saleState === "total_sales" ? "Dh 875" : "Dh 235";

    const currentOutputData = outputState === "monthly" ? monthlySaleOutputData : dailySaleOutputData;
    const currentOutputValue = outputState === "monthly" ? "5.2x" : "2.1x";
    const currentOutputChange = outputState === "monthly" ? "-15%" : "+8%";

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <Card className="w-full min-w-1/3 rounded-[8px] flex">
                <CardContent className="flex flex-col items-start justify-center px-4">
                    <CardHeader className="w-full py-2 px-0">
                        <CardTitle className="text-sm font-medium">Top-Selling Products</CardTitle>
                    </CardHeader>
                    <span className="text-2xl font-medium">Panadol 10mg</span>
                    <span className="text-sm font-normal text-gray-400">This item has been the top selling item for the last one month.</span>
                    <Button className="underline text-xs px-0" variant="link">Check Ranking <ArrowRight className="ml-[5px] w-3" /></Button>
                </CardContent>
            </Card>
            <Card className="w-full min-w-1/3 rounded-[8px] flex">
                <CardContent className="flex flex-col items-start justify-center px-4">
                    <CardHeader className="w-full py-2 px-0">
                        <CardTitle className="text-sm font-medium">Low Stock Alert</CardTitle>
                    </CardHeader>
                    <span className="text-2xl font-medium">28 items</span>
                    <span className="text-sm font-normal text-gray-400">Canestan and 15 more items.</span>
                    <Button className="underline text-xs px-0" variant="link">Check Inventory <ArrowRight className="ml-[5px] w-3" /></Button>
                </CardContent>
            </Card>
            <Card className="w-full col-span-1 md:col-span-1 lg:col-span-1 rounded-[8px] flex">
                <CardContent className="flex flex-col items-start justify-center px-4 gap-y-2 w-full">
                    <CardHeader className="w-full py-2 px-0">
                        <CardTitle className="flex flex-row items-center justify-start gap-x-2 font-normal"><Button className={cn("text-sm font-medium px-0 hover:bg-transparent hover:underline", saleState === "total_sales"?"text-black" : "text-gray-500")} variant="ghost"  onClick={() => setSaleState("total_sales")}>Average Sale Value</Button>|<Button className={cn("text-sm font-medium px-0 hover:bg-transparent hover:underline", saleState === "narcotic_sales"?"text-black" : "text-gray-500")} variant="ghost" onClick={() => setSaleState("narcotic_sales")}>Average Narcotic Sale Value</Button></CardTitle>
                    </CardHeader>
                    <span className="text-2xl font-semibold">{currentValue}</span>
                    <span className="text-sm font-normal text-gray-400">Last 30 days <span className="text-green-500"> +12%</span></span>
                    <div className="w-full h-[200px] mt-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={currentData} margin={{ top: 5, right: 5, left: 5, bottom: 15 }}>
                                <XAxis 
                                    dataKey="day" 
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fill: '#1A1A1A' }}
                                    tickFormatter={(value) => 'Day '+value}
                                />
                                <YAxis hide domain={['dataMin', 'dataMax']} />
                                <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="#3b82f6"
                                    strokeWidth={2.5}
                                    dot={false}
                                    activeDot={{ r: 4, fill: "#3b82f6", stroke: "#ffffff", strokeWidth: 2 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
            <Card className="w-full col-span-1 md:col-span-1 lg:col-span-1 rounded-[8px] flex">
                <CardContent className="flex flex-col items-start justify-center px-4 gap-y-2 w-full">
                    <CardHeader className="w-full py-2 px-0">
                        <CardTitle className="flex flex-row items-center justify-start gap-x-2 font-normal"><Button className={cn("text-sm font-medium px-0 hover:bg-transparent hover:underline", outputState === "monthly"?"text-black" : "text-gray-500")} variant="ghost"  onClick={() => setOutputState("monthly")}>Monthly Sale Output</Button>|<Button className={cn("text-sm font-medium px-0 hover:bg-transparent hover:underline", outputState === "daily"?"text-black" : "text-gray-500")} variant="ghost" onClick={() => setOutputState("daily")}>Daily Sale Output</Button></CardTitle>
                    </CardHeader>
                    <span className="text-2xl font-semibold">{currentOutputValue}</span>
                    <span className="text-sm font-normal text-gray-400">Last 30 days <span className={cn(currentOutputChange.startsWith("+") ? "text-green-500" : "text-red-500")}> {currentOutputChange}</span></span>
                    <div className="w-full h-[200px] mt-2">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={currentOutputData} margin={{ top: 5, right: 5, left: 5, bottom: 15 }}>
                                <XAxis 
                                    dataKey="period" 
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fontSize: 10, fill: '#1A1A1A' }}
                                    tickFormatter={(value) => outputState === "monthly" ? `M${value}` : `D${value}`}
                                />
                                <YAxis hide domain={['dataMin', 'dataMax']} />
                                <Bar
                                    dataKey="value"
                                    fill="#1A1A1A"
                                    radius={[2, 2, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default SalesAnalytics;